import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useContext } from 'react';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { AutoResizeTextarea } from '../../../../components/AutoResizeTextarea';

export default function NonADHDDiagnosticConfirmation({ canEdit = true, onDirty }) {
  const { patientId } = useParams();
  const { patientDataModel, updatePatientFirestore } = useContext(PatientDataContext);
  
  // Get provisional diagnoses from clinical interview
  const provisionalPrimaryDiagnosis = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[12]?.provisionalPrimaryDiagnosis || null;
  
  // State for the diagnostic decision
  const [diagnosisDecision, setDiagnosisDecision] = useState(null); // 'Yes' or 'No'
  const [diagnosisNotes, setDiagnosisNotes] = useState('');

  // Ref that always holds the latest diagnosisNotes — used by handleNotesBlur
  // to avoid reading a stale render closure when blur fires between the last
  // keystroke and the next React re-render.
  const diagnosisNotesRef = useRef(diagnosisNotes);
  useEffect(() => { diagnosisNotesRef.current = diagnosisNotes; }, [diagnosisNotes]);

  // One-shot hydration from Firestore — only runs on initial mount.
  // Does NOT re-run on patientDataModel refreshes triggered by writes to
  // other fields (which would clobber in-progress typed-but-unblurred notes).
  // Matches the isInitialLoad pattern used in PAD pages and CSD.
  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (!isInitialLoad.current) return;
    const diagnosticDecision = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.[1] || {};
    if (diagnosticDecision.nonAdhdDiagnosisDecision) {
      isInitialLoad.current = false;
      setDiagnosisDecision(diagnosticDecision.nonAdhdDiagnosisDecision);
      setDiagnosisNotes(diagnosticDecision.nonAdhdDiagnosisNotes || '');
    }
  }, [patientDataModel]);
  
  // Handle confirming the primary diagnosis
  const handleDiagnosisDecision = (decision) => {
    if (!canEdit) return;
    
    setDiagnosisDecision(decision);
    onDirty?.();
    
    // Update Firestore with the decision
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        sections: {
            '1': {
              nonAdhdDiagnosisDecision: decision,
              nonAdhdDiagnosisNotes: diagnosisNotes,
              nonAdhdDiagnosisCondition: provisionalPrimaryDiagnosis,              
              diagnosisDecision: 'Not met', // Set ADHD diagnosis as "Not met"
            }
        }
      }
    });
  };
  
  // Handle notes text changes — updates local state AND enqueues a
  // debounced Firestore write on every keystroke. This ensures that if the
  // user switches decision (unmounting the textarea) before blur fires, the
  // typed text is already queued for persistence. The 200ms debounce in
  // PatientDataContext naturally coalesces rapid keystrokes.
  const handleNotesChange = (text) => {
    if (!canEdit) return;
    
    setDiagnosisNotes(text);
    onDirty?.();

    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        sections: {
          '1': {
            nonAdhdDiagnosisNotes: text
          }
        }
      }
    });
  };
  
  // Save notes to Firestore when user finishes typing.
  // Reads from diagnosisNotesRef.current so we always persist the latest
  // keystroke even if blur fires before React re-renders with the newest
  // state (stale-closure guard).
  const handleNotesBlur = () => {
    if (!canEdit || !diagnosisDecision) return;
    
    onDirty?.();

    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': {
        sections: {
          '1': {
            nonAdhdDiagnosisNotes: diagnosisNotesRef.current
          }
        }
      }
    });
  };
  
  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
      <h2 className="text-left text-xl font-bold mb-6">Confirm Provisional Primary Diagnosis</h2>
      
      {/* Primary Diagnosis Confirmation */}
      <div className="mb-4 flex items-start">
        <div className="flex items-center gap-1 w-96">
          <h4 className="text-left mt-2 font-semibold text-gray-700">            
              {provisionalPrimaryDiagnosis || "No diagnosis recorded"}            
          </h4>
        </div>
        
        <div className="ml-5">
            <ul className="space-y-2 w-96">
              <li
                onClick={() => canEdit && handleDiagnosisDecision('Yes')}
                className={`
                  flex flex-col gap-3 p-2 
                  rounded-lg transition-all duration-300 
                  ${canEdit ? 'cursor-pointer group' : 'cursor-not-allowed opacity-75'}                                      
                  ${diagnosisDecision === 'Yes'
                    ? 'bg-violet-300 outline outline-2 outline-violet-500 font-semibold'
                    : 'bg-violet-100 hover:bg-violet-200'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <div className={`
                    flex-shrink-0 w-4 h-4 rounded-full border-2
                    transition-all duration-200 ease-in-out
                    ${diagnosisDecision === 'Yes'
                      ? 'border-violet-700 bg-violet-700'
                      : 'border-gray-300 group-hover:border-violet-400'
                    }
                    flex items-center justify-center
                  `}>
                    {diagnosisDecision === 'Yes' && (
                      <div className="w-2 h-2 bg-white rounded transition-all duration-200 ease-in-out" />
                    )}
                  </div>
                  <span className={`font-semibold transition-colors duration-200 ${diagnosisDecision === 'Yes' ? 'text-violet-900' : 'text-gray-700'}`}>
                    Yes
                  </span>
                </div>
                
                {diagnosisDecision === 'Yes' && (
                  <AutoResizeTextarea
                    placeholder={canEdit ? "Please provide rationale for confirming diagnosis" : "Notes can only be added by the assigned clinician"}
                    className={`font-medium w-full mt-1 p-2 border rounded-md ${!canEdit ? 'bg-gray-100 cursor-not-allowed opacity-75' : 'bg-white'} text-black min-h-[60px] resize-none overflow-hidden`}
                    value={diagnosisNotes}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    onBlur={() => handleNotesBlur()}
                    onClick={(e) => e.stopPropagation()}
                    disabled={!canEdit}
                  />
                )}
              </li>
              
              <li
                onClick={() => canEdit && handleDiagnosisDecision('No')}
                className={`
                  flex flex-col gap-3 p-2 
                  rounded-lg transition-all duration-300 
                  ${canEdit ? 'cursor-pointer group' : 'cursor-not-allowed opacity-75'}                                        
                  ${diagnosisDecision === 'No'
                    ? 'bg-violet-300 outline outline-2 outline-violet-500 font-semibold'
                    : 'bg-violet-100 hover:bg-violet-200'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <div className={`
                    flex-shrink-0 w-4 h-4 rounded-full border-2
                    transition-all duration-200 ease-in-out
                    ${diagnosisDecision === 'No'
                      ? 'border-violet-700 bg-violet-700'
                      : 'border-gray-300 group-hover:border-violet-400'
                    }
                    flex items-center justify-center
                  `}>
                    {diagnosisDecision === 'No' && (
                      <div className="w-2 h-2 bg-white rounded transition-all duration-200 ease-in-out" />
                    )}
                  </div>
                  <span className={`font-semibold transition-colors duration-200 ${diagnosisDecision === 'No' ? 'text-violet-900' : 'text-gray-700'}`}>
                    No
                  </span>
                </div>
                
                {diagnosisDecision === 'No' && (
                  <AutoResizeTextarea
                    placeholder={canEdit ? "Please provide rationale for not confirming diagnosis and recommended next steps" : "Notes can only be added by the assigned clinician"}
                    className={`font-medium w-full mt-1 p-2 border rounded-md ${!canEdit ? 'bg-gray-100 cursor-not-allowed opacity-75' : 'bg-white'} text-black min-h-[60px] resize-none overflow-hidden`}
                    value={diagnosisNotes}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    onBlur={() => handleNotesBlur()}
                    onClick={(e) => e.stopPropagation()}
                    disabled={!canEdit}
                  />
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>            
  );
}